// Master files: a blank "Master - <client>.xlsx" copied from the firm's
// template into the master-file folder in SharePoint, and linked to the client.
// The server does the work (POST /api/sharepoint/clients/<id>/master-file/);
// this module is the shared front end for it -- the "Add Master File" tick box
// on both New Client forms, and the step that runs once a client exists.
import React, { useEffect, useState } from "react";
import { apiErrorMessage, apiGet, apiPost } from "../api";

export const masterFileUrl = (clientId) =>
  `/api/sharepoint/clients/${clientId}/master-file/`;

// Whether a manager has picked the folder and template in Settings for this
// kind of client: T2 (corporate) and T1 (personal) each have their own pair.
// `isCorporate` undefined means "either" (the client isn't known yet).
// null while loading; false on any failure, so the tick box degrades to the
// "not set up" hint rather than offering a step that would fail.
export function useMasterFileConfigured(isCorporate) {
  const [fs, setFs] = useState(null);
  useEffect(() => {
    let live = true;
    // Promise.resolve().then so a mocked apiGet returning a non-promise
    // cannot throw synchronously inside the effect.
    Promise.resolve()
      .then(() => apiGet("/api/firm-settings/"))
      .then((data) => { if (live) setFs(data || {}); })
      .catch(() => { if (live) setFs({}); });
    return () => { live = false; };
  }, []);
  if (fs === null) return null;
  const key = isCorporate === undefined ? null
    : isCorporate ? "master_file_configured_corporate" : "master_file_configured_personal";
  // A server without the per-type flags only says "configured".
  if (key && key in fs) return Boolean(fs[key]);
  return Boolean(fs.master_file_configured);
}

// Never throws: the client already exists by the time this runs, and a
// SharePoint failure must not read as "the client was not created".
export async function addMasterFile(clientId) {
  try {
    const res = await apiPost(masterFileUrl(clientId), {});
    return { ok: true, message: res?.detail || "Master file created and linked.", file: res?.file };
  } catch (e) {
    return {
      ok: false,
      message: typeof apiErrorMessage === "function"
        ? apiErrorMessage(e, "The master file could not be created.")
        : e?.message || "The master file could not be created.",
    };
  }
}

// The tick box. `checked` is null until the user touches it, meaning "follow
// the default" -- ticked exactly when master files are set up.
export function AddMasterFileCheckbox({ configured, checked, onChange, isCorporate }) {
  const kind = isCorporate === undefined ? "" : isCorporate ? "T2 (corporate) " : "T1 (personal) ";
  const effective = checked ?? Boolean(configured);
  const disabled = !configured;
  return (
    <div>
      <label
        style={{
          display: "flex", alignItems: "center", gap: 8,
          cursor: disabled ? "default" : "pointer",
          color: disabled ? "#9ca3af" : "#323338", fontSize: 14,
        }}
      >
        <input
          type="checkbox"
          aria-label="Add Master File"
          checked={disabled ? false : effective}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        Add Master File
      </label>
      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4, marginLeft: 24 }}>
        {configured === null
          ? "Checking master file setup…"
          : configured
            ? `Creates a folder named after the client in the ${kind}master file folder, with a blank master workbook in it, and links it to the client.`
            : `Not set up yet: a manager picks the ${kind}master file folder and template in Settings → Master files.`}
      </div>
    </div>
  );
}

// What New Client should do, given the tick box's state.
export const wantsMasterFile = (configured, checked) =>
  Boolean(configured) && (checked ?? true);
